/**
 * The `match-report-v2` route gate, shared by every match-report route.
 *
 * Extracted from `app/(root)/quick-capture/_layout.tsx` when the submitted-report
 * route (SE-6985) needed the identical gate. It is not a generic feature-flag
 * hook — it is THIS flag's decision procedure, and the three failure modes it
 * encodes were each paid for once:
 *
 *  - `undefined` is "still loading", not "off". Deciding on it bounces users off
 *    a route they are entitled to.
 *  - a resolved `false` is not decisive until PostHog has identified the user:
 *    before identify, `/flags` answers for the ANONYMOUS distinct id, and a
 *    user-targeted flag comes back a real `false` (SE-6233).
 *  - the wait is BOUNDED. Ad blockers routinely block `us.i.posthog.com`, and an
 *    unbounded wait renders a permanently blank page (SE-5592). On timeout the
 *    gate FAILS CLOSED — same path as an explicit `false`.
 *
 * Note this flag is separate from the backend's AppConfig `matchReportV2`; the
 * two can disagree, and passing this gate does not guarantee the GraphQL
 * operations are enabled.
 * @module hooks/useMatchReportV2Gate
 */

import { useEffect, useState } from "react";

import { POSTHOG_FLAGS } from "@/config/featureFlags";
import { useCurrentUser } from "@/features/auth/providers/CurrentUser";
import { usePostHogIdentified } from "@/hooks/posthogIdentityStore";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";

/**
 * Upper bound for the gate's loading window — long enough for a slow PostHog
 * `/flags` round-trip plus authMe re-hydration, short enough that a blocked
 * PostHog does not strand the user on a spinner.
 */
export const GATE_TIMEOUT_MS = 8_000;

/** What the gate has decided. */
export type MatchReportGateStatus = "loading" | "allowed" | "denied";

/**
 * Decides whether the current user may enter a match-report route.
 * @returns `"loading"` while undecided, then `"allowed"` or `"denied"`.
 */
const useMatchReportV2Gate = (): MatchReportGateStatus => {
  const currentUser = useCurrentUser();
  const flagValue = usePostHogFeatureFlag(POSTHOG_FLAGS.MATCH_REPORT_V2);
  const isCaptureEnabled = flagValue === true;

  const isUserLoading = currentUser === undefined;
  const isIdentified = usePostHogIdentified();
  const isAwaitingIdentify = currentUser != null && !isIdentified;
  const isFlagLoading = flagValue === undefined || isAwaitingIdentify;
  const isGateLoading = isUserLoading || isFlagLoading;

  // The fail-closed timer covers ONE failure mode: PostHog never answering. It
  // must not be armed while the identity is still in flight — authMe can
  // outlast the deadline on a cold load, and expiring then ejects a user from
  // a route they are entitled to.
  const isFlagTimerArmed = isFlagLoading && !isUserLoading;

  // Reset synchronously during render rather than in an effect: an effect runs
  // after commit, so a stale `hasTimedOut` would render a redirect for one
  // frame — and expo-router navigates from its own effect, so that frame is a
  // real navigation, not a harmless flicker.
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [prevIsTimerArmed, setPrevIsTimerArmed] = useState(isFlagTimerArmed);
  if (isFlagTimerArmed !== prevIsTimerArmed) {
    setPrevIsTimerArmed(isFlagTimerArmed);
    if (isFlagTimerArmed) setHasTimedOut(false);
  }

  /** PostHog never answered within the deadline — the only fail-closed case. */
  const isFlagStalled = isFlagTimerArmed && hasTimedOut;

  useEffect(() => {
    if (!isFlagTimerArmed) return undefined;
    const timer = setTimeout(() => setHasTimedOut(true), GATE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isFlagTimerArmed]);

  if (isGateLoading && !isFlagStalled) return "loading";

  return isFlagStalled || !isCaptureEnabled ? "denied" : "allowed";
};

export default useMatchReportV2Gate;
